import React, {useCallback, useEffect, useState} from 'react'
import {Button, Card, Col, Form, Modal, Row, Spinner} from 'react-bootstrap'
import {useDropzone} from 'react-dropzone'
import { checkPermission } from '../../../utils/checkPermission';
import { PageTitle } from '../../../../_metronic/layout/core';
import { KTSVG, showToast } from '../../../../_metronic/helpers';
import { ToastType } from '../../../../_metronic/helpers/crud-helper/ToastTypes';
import {
  Banner,
  bannerImageUrl,
  deleteBanner,
  getBanners,
  saveBanner,
  uploadBannerImage,
} from './core/_requests'

const MAX_BANNERS = 5
const RECOMMENDED_WIDTH = 1200
const RECOMMENDED_HEIGHT = 500
const MAX_FILE_SIZE_MB = 5
// Allowed deviation in px from the recommended size - keep in sync with API bannerImageValidator.js
const DIMENSION_TOLERANCE = 5
const ALLOWED_FORMATS = 'PNG, JPG, JPEG'
const BANNER_SLOTS = Array.from({length: MAX_BANNERS}, (_, i) => i + 1)

const isValidUrl = (value: string) => /^https?:\/\/\S+$/i.test(value.trim())

const readImageSize = (file: File): Promise<{width: number; height: number}> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      resolve({width: img.width, height: img.height})
      URL.revokeObjectURL(objectUrl)
    }
    img.onerror = () => {
      reject(new Error('Invalid image'))
      URL.revokeObjectURL(objectUrl)
    }
    img.src = objectUrl
  })

type BannerSlotProps = {
  index: number
  banner?: Banner
  defaultBanner?: Banner
  canUpdate: boolean
  canDelete: boolean
  onSaved: (banner: Banner) => void
  onDeleted: () => void
  onView: (url: string) => void
}

const BannerSlot = ({
  index,
  banner,
  defaultBanner,
  canUpdate,
  canDelete,
  onSaved,
  onDeleted,
  onView,
}: BannerSlotProps) => {
  // image_name uploaded but not yet saved against this slot
  const [pendingImage, setPendingImage] = useState<string>('')
  // Uploaded banner's link, else the default banner's link for this slot
  const savedRedirectUrl = (banner ? banner.redirect_url : defaultBanner?.redirect_url) || ''
  const [redirectUrl, setRedirectUrl] = useState<string>(savedRedirectUrl)
  const [urlError, setUrlError] = useState<string>('')
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    setRedirectUrl(savedRedirectUrl)
  }, [savedRedirectUrl])

  const currentImage = pendingImage || banner?.image || ''
  // Shown read-only when the slot is empty - the app falls back to this banner
  const showDefault = !currentImage && !!defaultBanner
  const isDirty = !!pendingImage || (!!banner && (banner.redirect_url || '') !== redirectUrl.trim())

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      showToast({
        message: `Please upload an image in ${ALLOWED_FORMATS} format with a maximum file size of ${MAX_FILE_SIZE_MB} MB.`,
        type: ToastType.ERROR,
        autoClose: 3000,
      })
      return
    }
    const file = acceptedFiles[0]
    if (!file) return

    let sizeWarning = ''
    try {
      // Out-of-range dimensions are rejected by the API, which returns the error message shown below
      const {width, height} = await readImageSize(file)
      if (width !== RECOMMENDED_WIDTH || height !== RECOMMENDED_HEIGHT) {
        sizeWarning = `Image is ${width} x ${height} px. Recommended size is ${RECOMMENDED_WIDTH} x ${RECOMMENDED_HEIGHT} px (2.4:1); it may be cropped in the app.`
      }
    } catch (err) {
      showToast({message: 'Selected file is not a valid image.', type: ToastType.ERROR, autoClose: 3000})
      return
    }

    setUploading(true)
    try {
      const response = await uploadBannerImage(file)
      if (response?.newFilename) {
        setPendingImage(response.newFilename)
        // Warn only once the backend has accepted the image
        if (sizeWarning) {
          showToast({message: sizeWarning, type: ToastType.WARNING, autoClose: 4000})
        }
      } else {
        // Backend validation error (format / size / dimensions)
        showToast({
          message: response?.responseMessage || 'Something Went Wrong',
          type: ToastType.ERROR,
          autoClose: 4000,
        })
      }
    } catch (err) {
      showToast({message: 'Image upload failed', type: ToastType.ERROR})
    } finally {
      setUploading(false)
    }
  }, [])

  const {getRootProps, getInputProps, open, isDragActive} = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    maxSize: MAX_FILE_SIZE_MB * 1024 * 1024,
    disabled: !canUpdate || uploading || saving,
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
  })

  const handleSave = async () => {
    if (!currentImage) {
      showToast({message: 'Please upload a banner image.', type: ToastType.ERROR})
      return
    }
    if (redirectUrl.trim() && !isValidUrl(redirectUrl)) {
      setUrlError('Please enter a valid URL starting with http:// or https://')
      return
    }

    setSaving(true)
    try {
      const response = await saveBanner(index, {
        image_name: currentImage,
        redirect_url: redirectUrl.trim(),
      })
      if (response?.responseStatus === 200) {
        setPendingImage('')
        onSaved(response.responseData)
        showToast({message: response.responseMessage})
      } else {
        showToast({
          message: response?.responseData?.msg || response?.responseMessage || 'Something Went Wrong',
          type: ToastType.ERROR,
          autoClose: 3000,
        })
      }
    } catch (err) {
      showToast({message: 'Something Went Wrong', type: ToastType.ERROR})
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setShowDeleteConfirm(false)
    // Unsaved upload on an empty slot - just discard it
    if (!banner) {
      setPendingImage('')
      setRedirectUrl(savedRedirectUrl)
      return
    }

    setSaving(true)
    try {
      const response = await deleteBanner(index)
      if (response?.responseStatus === 200) {
        setPendingImage('')
        onDeleted()
        showToast({message: response.responseMessage})
      } else {
        showToast({message: response?.responseMessage || 'Something Went Wrong', type: ToastType.ERROR})
      }
    } catch (err) {
      showToast({message: 'Something Went Wrong', type: ToastType.ERROR})
    } finally {
      setSaving(false)
    }
  }

  const busy = uploading || saving

  return (
    <Card className='shadow-sm mb-6'>
      <Card.Header className='min-h-50px'>
        <Card.Title className='fs-5'>
          Banner {index}
          {banner ? (
            <span className='badge badge-light-success ms-3'>Active</span>
          ) : showDefault ? (
            <span className='badge badge-light-info ms-3'>Default</span>
          ) : (
            <span className='badge badge-light ms-3'>Empty</span>
          )}
          {pendingImage && <span className='badge badge-light-warning ms-2'>Unsaved</span>}
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <Row className='g-6 align-items-center'>
          <Col lg={6}>
            <div
              {...getRootProps({
                className: `dropzone p-0 overflow-hidden ${isDragActive ? 'border-primary' : ''}`,
                onClick: canUpdate && !busy ? open : undefined,
              })}
              style={{aspectRatio: '2.4 / 1', cursor: canUpdate ? 'pointer' : 'default', minHeight: 0}}
            >
              <input {...getInputProps()} />
              {busy ? (
                <div className='d-flex h-100 align-items-center justify-content-center'>
                  <Spinner animation='border' variant='primary' />
                </div>
              ) : currentImage ? (
                <img
                  src={bannerImageUrl(currentImage)}
                  alt={`Banner ${index}`}
                  className='w-100 h-100'
                  style={{objectFit: 'cover'}}
                />
              ) : showDefault ? (
                <div className='position-relative w-100 h-100'>
                  <img
                    src={defaultBanner!.image_url}
                    alt={`Default banner ${index}`}
                    className='w-100 h-100'
                    style={{objectFit: 'cover', opacity: 0.6}}
                  />
                  <div className='position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-75 text-white fs-7 py-2 px-3'>
                    Default banner in use{canUpdate ? ' · drag & drop or click to upload your own' : ''}
                  </div>
                </div>
              ) : (
                <div className='d-flex h-100 flex-column align-items-center justify-content-center text-center px-4'>
                  <KTSVG path='/media/icons/duotune/files/fil009.svg' className='svg-icon-primary svg-icon-2hx' />
                  <div className='fs-6 fw-bold text-gray-900 pt-3'>
                    {canUpdate ? 'Drag & drop banner here or click to select' : 'No banner uploaded'}
                  </div>
                  <div className='fs-7 text-muted pt-1'>
                    Required: {RECOMMENDED_WIDTH} x {RECOMMENDED_HEIGHT} px (2.4:1) · {ALLOWED_FORMATS} · max{' '}
                    {MAX_FILE_SIZE_MB} MB
                  </div>
                </div>
              )}
            </div>
          </Col>
          <Col lg={6}>
            <Form.Group className='mb-5'>
              <Form.Label className='form-label'>Redirection URL</Form.Label>
              <Form.Control
                type='url'
                placeholder='https://example.com/page'
                value={redirectUrl}
                readOnly={!canUpdate}
                isInvalid={!!urlError}
                onChange={(e) => {
                  setRedirectUrl(e.target.value)
                  setUrlError('')
                }}
              />
              <Form.Control.Feedback type='invalid'>{urlError}</Form.Control.Feedback>
            </Form.Group>
            <div className='text-muted fs-7 mb-5'>
              Required size: {RECOMMENDED_WIDTH} px (W) x {RECOMMENDED_HEIGHT} px (H), aspect ratio 2.4:1
              <br />
              Formats: {ALLOWED_FORMATS} · Max file size: {MAX_FILE_SIZE_MB} MB
            </div>
            <div className='d-flex flex-wrap gap-3'>
              {canUpdate && (
                <Button variant='light-primary' size='sm' onClick={open} disabled={busy}>
                  <i className='bi bi-upload me-1'></i>
                  {currentImage ? 'Replace' : 'Upload'}
                </Button>
              )}
              <Button
                variant='light-info'
                size='sm'
                disabled={(!currentImage && !showDefault) || busy}
                onClick={() => onView(currentImage ? bannerImageUrl(currentImage) : defaultBanner!.image_url)}
              >
                <i className='bi bi-eye me-1'></i>View
              </Button>
              {canDelete && (
                <Button
                  variant='light-danger'
                  size='sm'
                  disabled={!currentImage || busy}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <i className='bi bi-trash me-1'></i>Delete
                </Button>
              )}
              {canUpdate && (
                <Button variant='primary' size='sm' disabled={!currentImage || !isDirty || busy} onClick={handleSave}>
                  {saving ? <Spinner animation='border' size='sm' /> : 'Save'}
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Card.Body>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Banner {index}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {banner
            ? `Are you sure you want to delete banner ${index}? The app will show the default banner for this slot.`
            : 'Discard the uploaded image for this slot?'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant='danger' onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  )
}

const BannersPage: React.FC = () => {
  const [banners, setBanners] = useState<{[index: number]: Banner}>({})
  const [defaultBanners, setDefaultBanners] = useState<{[index: number]: Banner}>({})
  const [loading, setLoading] = useState(true)
  const [viewUrl, setViewUrl] = useState<string>('')

  const canUpdate = checkPermission('Banners', 'update')
  const canDelete = checkPermission('Banners', 'delete')

  const fetchBanners = useCallback(async () => {
    try {
      const response = await getBanners()
      const list: Banner[] = Array.isArray(response?.responseData) ? response.responseData : []
      const byIndex: {[index: number]: Banner} = {}
      const defaultsByIndex: {[index: number]: Banner} = {}
      list.forEach((banner) => {
        if (banner.is_default) {
          defaultsByIndex[banner.index] = banner
        } else {
          byIndex[banner.index] = banner
        }
      })
      setBanners(byIndex)
      setDefaultBanners(defaultsByIndex)
    } catch (err) {
      showToast({message: 'Unable to load banners', type: ToastType.ERROR})
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBanners()
  }, [fetchBanners])

  const handleSaved = (banner: Banner) => setBanners((prev) => ({...prev, [banner.index]: banner}))

  // Reload so the slot picks up its default banner (image + redirect url) from the API
  const handleDeleted = () => fetchBanners()

  return (
    <>
      <PageTitle>Banner Management</PageTitle>
      <div className='alert alert-primary d-flex align-items-center p-5 mb-6'>
        <i className='bi bi-info-circle fs-2 text-primary me-4'></i>
        <div>
          {MAX_BANNERS} banners are shown in the mobile app in the order below (1 to {MAX_BANNERS}). Any slot
          without an uploaded banner shows its default banner.
          Required size: <b>{RECOMMENDED_WIDTH} x {RECOMMENDED_HEIGHT} px</b> (aspect ratio 2.4:1). Supported formats: <b>{ALLOWED_FORMATS}</b>. Max file size:{' '}
          <b>{MAX_FILE_SIZE_MB} MB</b>.
        </div>
      </div>
      {loading ? (
        <div className='d-flex justify-content-center py-10'>
          <Spinner animation='border' variant='primary' />
        </div>
      ) : (
        BANNER_SLOTS.map((index) => (
          <BannerSlot
            key={index}
            index={index}
            banner={banners[index]}
            defaultBanner={defaultBanners[index]}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onSaved={handleSaved}
            onDeleted={handleDeleted}
            onView={setViewUrl}
          />
        ))
      )}

      <Modal show={!!viewUrl} onHide={() => setViewUrl('')} size='xl' centered>
        <Modal.Header closeButton>
          <Modal.Title>Banner Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center'>
          {viewUrl && <img src={viewUrl} alt='Banner preview' className='img-fluid' />}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default BannersPage
